import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';

const BUCKET_NAME = 'public_documents';

const DokumenAdmin = () => {
    const { userRole } = useAuth();
    const isAdminNasional = userRole === 'admin_nasional';

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [form, setForm] = useState({ title: '' });
    const fileInputRef = useRef(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const { data: result, error } = await supabase
                .from('dokumen')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setData(result || []);
        } catch (error) {
            console.error('Error fetching dokumen:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Ekstraktor Nama File dari URL Publik
    const extractFileName = (url) => {
        if (!url) return null;
        const parts = url.split(`${BUCKET_NAME}/`);
        return parts.length > 1 ? parts[1] : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isAdminNasional) return;

        const file = fileInputRef.current.files[0];
        if (!file) return alert('Silakan pilih file PDF terlebih dahulu.');

        // Validasi Klien: Khusus PDF & Max 5MB
        if (file.type !== 'application/pdf') {
            return alert('Format dokumen wajib PDF.');
        }
        if (file.size > 5 * 1024 * 1024) {
            return alert('Ukuran dokumen maksimal 5MB.');
        }

        try {
            setIsSubmitting(true);

            // 1. Upload ke Storage
            // Sanitasi nama file agar kompatibel dengan URL
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
            const fileName = `${Date.now()}_${sanitizedName}`;

            const { error: uploadError } = await supabase.storage
                .from(BUCKET_NAME)
                .upload(fileName, file);

            if (uploadError) throw new Error(`Upload gagal: ${uploadError.message}`);

            // 2. Dapatkan URL
            const { data: urlData } = supabase.storage
                .from(BUCKET_NAME)
                .getPublicUrl(fileName);

            // 3. Insert ke Database
            const { error: dbError } = await supabase
                .from('dokumen')
                .insert([{
                    title: form.title,
                    file_url: urlData.publicUrl
                }]);

            if (dbError) throw new Error(`Gagal menyimpan database: ${dbError.message}`);

            setForm({ title: '' });
            fileInputRef.current.value = ''; // Reset file input
            fetchData();
            alert('Dokumen berhasil diunggah!');

        } catch (error) {
            console.error('Error proses dokumen:', error);
            alert(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id, fileUrl) => {
        if (!isAdminNasional || !window.confirm('Yakin ingin menghapus dokumen publik ini? (File PDF akan terhapus permanen dari server)')) return;

        try {
            // 1. Ekstrak nama file & Hapus dari Storage
            const fileName = extractFileName(fileUrl);
            if (fileName) {
                const { error: storageError } = await supabase.storage
                    .from(BUCKET_NAME)
                    .remove([fileName]);

                if (storageError) console.warn('Gagal menghapus file storage (mungkin sudah terhapus):', storageError);
            }

            // 2. Hapus baris Database
            const { error: dbError } = await supabase
                .from('dokumen')
                .delete()
                .eq('id', id);

            if (dbError) throw dbError;

            setData((prev) => prev.filter((item) => item.id !== id));
        } catch (error) {
            console.error('Error deleting dokumen:', error);
            alert('Gagal menghapus dokumen.');
        }
    };

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Pusat Unduhan & Dokumen</h1>
            <p className="text-gray-600 mb-6">Kelola file PDF resmi untuk diakses oleh publik.</p>

            <div className="flex flex-col md:flex-row gap-6">
                {/* Form Tambah */}
                {isAdminNasional && (
                    <div className="w-full md:w-1/3 bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-fit">
                        <h2 className="text-lg font-semibold mb-4 text-gray-700">Unggah Dokumen Baru</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nama Dokumen</label>
                                <input
                                    type="text"
                                    required
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    placeholder="Mis: AD/ART HEBITREN 2026"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">File (Khusus PDF, Maks 5MB)</label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    required
                                    accept="application/pdf"
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-md"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-blue-600 text-white font-medium py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-70 transition-colors"
                            >
                                {isSubmitting ? 'Mengunggah...' : 'Unggah File'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Tabel Data (Read-Only) */}
                <div className={`w-full ${isAdminNasional ? 'md:w-2/3' : 'w-full'} bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden`}>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Informasi Dokumen</th>
                                    {isAdminNasional && <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr><td colSpan={isAdminNasional ? 2 : 1} className="px-6 py-8 text-center text-gray-500">Memuat dokumen...</td></tr>
                                ) : data.length === 0 ? (
                                    <tr><td colSpan={isAdminNasional ? 2 : 1} className="px-6 py-8 text-center text-gray-500">Belum ada dokumen yang diunggah.</td></tr>
                                ) : (
                                    data.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-start">
                                                    <div className="flex-shrink-0 pt-1">
                                                        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-3">
                                                        <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                                                        <a href={item.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 block">
                                                            Buka / Unduh File PDF
                                                        </a>
                                                        <span className="text-xs text-gray-400 block mt-1">Diupload: {new Date(item.created_at).toLocaleDateString('id-ID')}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            {isAdminNasional && (
                                                <td className="px-6 py-4 text-sm font-medium text-center">
                                                    <button
                                                        onClick={() => handleDelete(item.id, item.file_url)}
                                                        className="text-red-600 hover:text-red-900 transition-colors"
                                                    >
                                                        Hapus
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DokumenAdmin;